import { Box, Typography } from '@mui/material'
import { keyframes } from '@mui/system'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import { beoflowColors, borderRadius } from '@/theme/muiTheme'
import { createColorPill } from '@/theme/glassEffects'
import { useLiveUpdatesState } from '@/contexts/LiveUpdatesContext'

const pulse = keyframes`
  0%   { opacity: 0.5; }
  50%  { opacity: 1; }
  100% { opacity: 0.5; }
`

/**
 * Persistent header pill showing that an MCP agent is connected to the user's
 * session. Idle-but-connected shows a steady teal dot + "AI agent connected";
 * while the agent is writing it escalates to a pulsing orange "AI is editing
 * your <surface>…". Renders nothing when no agent is active (activity-decay).
 */
export default function AgentStatusPill() {
  const { connected, presence } = useLiveUpdatesState()
  if (!connected) return null

  const working = presence?.state === 'working'
  const color = working ? beoflowColors.peakOrange : beoflowColors.biancchiTeal
  const label = working
    ? `AI is editing ${presence?.detail ? `your ${presence.detail}` : 'your data'}…`
    : 'AI agent connected'

  return (
    <Box
      role="status"
      aria-live="polite"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        px: 1.25,
        py: 0.5,
        whiteSpace: 'nowrap',
        ...createColorPill(color),
      }}
    >
      {working ? (
        <AutoAwesomeIcon sx={{ fontSize: 16, color, animation: `${pulse} 1.4s ease-in-out infinite` }} />
      ) : (
        <Box sx={{ width: 8, height: 8, borderRadius: borderRadius.round, bgcolor: color, flexShrink: 0 }} />
      )}
      <Typography variant="monoLabel" sx={{ color: 'text.primary', lineHeight: 1 }}>
        {label}
      </Typography>
    </Box>
  )
}
